import { shouldOffer } from './callModel';

/**
 * The WebRTC side of a call: one RTCPeerConnection per other person (a mesh — no SFU, no
 * media server, nothing paid), Opus audio and VP8/H.264 video over DTLS-SRTP, direct where
 * the network allows and through our own coturn where it doesn't.
 *
 * Signalling goes out through `send(peerId, type, payload)` (the messages hub) and comes back
 * through `handleSignal`. Negotiation is the "perfect negotiation" pattern: whoever adds a
 * track offers; when both offer at once the polite side (the larger id — see shouldOffer)
 * backs down. That one rule covers the first connection, a voice call turning into video,
 * and a camera flip, without a separate state machine for each.
 *
 * Audio levels (your own and each peer's) come off an AnalyserNode at ~6 Hz; the route label
 * comes off getStats' selected candidate pair.
 */
export default class PeerMesh {
  constructor({ myId, iceServers = [], send, onTrack, onPeerState, onLevels, onRoute }) {
    this.myId = myId;
    this.iceServers = iceServers;
    this.send = send;
    this.onTrack = onTrack;
    this.onPeerState = onPeerState;
    this.onLevels = onLevels;
    this.onRoute = onRoute;
    this.peers = new Map();        // peerId → { pc, stream, makingOffer, ignoreOffer, polite, pendingIce: [], senders: {} }
    this.pendingSignals = new Map(); // peerId → [{ type, payload }] for signals that beat addPeer
    this.localStream = null;
    this.audioCtx = null;
    this.analysers = new Map();    // 'me' | peerId → { analyser, data }
    this.levelTimer = null;
    this.routeTimer = null;
    this.closed = false;
  }

  setLocalStream(stream) {
    this.localStream = stream;
    for (const [, p] of this.peers) this.#syncTracks(p);
    this.#watchLevels('me', stream);
  }

  /** Swap the camera (a flip) or add one (voice → video) on every connection. */
  async setVideoTrack(track) {
    if (!this.localStream) return;
    this.localStream.getVideoTracks().forEach((t) => { if (t !== track) { this.localStream.removeTrack(t); t.stop(); } });
    if (track && !this.localStream.getVideoTracks().includes(track)) this.localStream.addTrack(track);
    for (const [, p] of this.peers) {
      const sender = p.pc.getSenders().find((s) => s.track?.kind === 'video' || (s.track === null && p.videoSender === s));
      if (track && sender) { await sender.replaceTrack(track).catch(() => {}); p.videoSender = sender; }
      else if (track) { p.videoSender = p.pc.addTrack(track, this.localStream); }
      else if (sender) { await sender.replaceTrack(null).catch(() => {}); }
    }
  }

  addPeer(peerId) {
    if (this.closed || this.peers.has(peerId)) return this.peers.get(peerId);
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const p = { pc, stream: new MediaStream(), makingOffer: false, ignoreOffer: false, polite: !shouldOffer(this.myId, peerId), pendingIce: [], videoSender: null };
    this.peers.set(peerId, p);
    pc.onicecandidate = (e) => { if (e.candidate) this.send(peerId, 'ice', e.candidate.toJSON()); };
    pc.ontrack = (e) => {
      e.streams[0]?.getTracks().forEach((t) => { if (!p.stream.getTracks().includes(t)) p.stream.addTrack(t); });
      if (!e.streams[0]) p.stream.addTrack(e.track);
      this.onTrack?.(peerId, p.stream);
      this.#watchLevels(peerId, p.stream);
    };
    pc.onnegotiationneeded = async () => {
      try {
        p.makingOffer = true;
        await pc.setLocalDescription();
        this.send(peerId, 'offer', pc.localDescription);
      } catch (err) { /* a closed connection mid-offer */ } finally { p.makingOffer = false; }
    };
    pc.onconnectionstatechange = () => {
      this.onPeerState?.(peerId, pc.connectionState);
      if (pc.connectionState === 'connected') this.#startRouteWatch();
    };
    pc.oniceconnectionstatechange = () => { if (pc.iceConnectionState === 'failed') pc.restartIce?.(); };
    this.#syncTracks(p);
    const queued = this.pendingSignals.get(peerId);
    if (queued) { this.pendingSignals.delete(peerId); queued.forEach(({ type, payload }) => this.handleSignal(peerId, type, payload)); }
    return p;
  }

  #syncTracks(p) {
    if (!this.localStream) return;
    const have = new Set(p.pc.getSenders().map((s) => s.track).filter(Boolean));
    this.localStream.getTracks().forEach((t) => { if (!have.has(t)) { const s = p.pc.addTrack(t, this.localStream); if (t.kind === 'video') p.videoSender = s; } });
  }

  async handleSignal(peerId, type, payload) {
    const p = this.peers.get(peerId);
    if (!p) {
      const list = this.pendingSignals.get(peerId) || [];
      list.push({ type, payload });
      this.pendingSignals.set(peerId, list);
      return;
    }
    const { pc } = p;
    try {
      if (type === 'offer' || type === 'answer') {
        const desc = payload;
        const collision = desc.type === 'offer' && (p.makingOffer || pc.signalingState !== 'stable');
        p.ignoreOffer = !p.polite && collision;
        if (p.ignoreOffer) return;
        await pc.setRemoteDescription(desc);
        for (const c of p.pendingIce.splice(0)) await pc.addIceCandidate(c).catch(() => {});
        if (desc.type === 'offer') {
          await pc.setLocalDescription();
          this.send(peerId, 'answer', pc.localDescription);
        }
      } else if (type === 'ice') {
        if (!pc.remoteDescription) { p.pendingIce.push(payload); return; }
        try { await pc.addIceCandidate(payload); } catch (err) { if (!p.ignoreOffer) throw err; }
      }
    } catch (err) {
      // A malformed or late description shouldn't take the call down; the next negotiation heals it.
      console.warn('call signal', type, err?.message || err);
    }
  }

  removePeer(peerId) {
    const p = this.peers.get(peerId);
    if (!p) return;
    try { p.pc.close(); } catch { /* already closed */ }
    this.peers.delete(peerId);
    this.analysers.delete(peerId);
  }

  peerIds() { return Array.from(this.peers.keys()); }

  // ── Levels ──

  #ctx() {
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      try { this.audioCtx = new Ctx(); } catch { return null; }
    }
    if (this.audioCtx.state === 'suspended') this.audioCtx.resume().catch(() => {});
    return this.audioCtx;
  }

  #watchLevels(key, stream) {
    const ctx = this.#ctx();
    if (!ctx || !stream?.getAudioTracks().length) return;
    const existing = this.analysers.get(key);
    if (existing?.stream === stream) return;
    try {
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      this.analysers.set(key, { analyser, data: new Uint8Array(analyser.frequencyBinCount), stream });
    } catch { /* a stream the context can't read */ }
    if (!this.levelTimer) {
      this.levelTimer = setInterval(() => {
        const out = {};
        for (const [k, a] of this.analysers) {
          a.analyser.getByteTimeDomainData(a.data);
          let sum = 0;
          for (let i = 0; i < a.data.length; i += 1) { const v = (a.data[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / a.data.length);
          out[k] = Math.min(1, rms * 4);
        }
        this.onLevels?.(out);
      }, 160);
    }
  }

  // ── Route ──

  #startRouteWatch() {
    if (this.routeTimer) return;
    const read = async () => {
      let relayed = false;
      let any = false;
      for (const [, p] of this.peers) {
        try {
          const stats = await p.pc.getStats();
          let pair = null;
          stats.forEach((r) => { if (r.type === 'transport' && r.selectedCandidatePairId) pair = stats.get(r.selectedCandidatePairId); });
          if (!pair) stats.forEach((r) => { if (r.type === 'candidate-pair' && (r.selected || r.nominated) && r.state === 'succeeded') pair = r; });
          if (!pair) continue;
          const local = stats.get(pair.localCandidateId);
          const remote = stats.get(pair.remoteCandidateId);
          any = true;
          if (local?.candidateType === 'relay' || remote?.candidateType === 'relay') relayed = true;
        } catch { /* stats unavailable mid-teardown */ }
      }
      if (any) this.onRoute?.(relayed ? 'relayed' : 'direct');
    };
    read();
    this.routeTimer = setInterval(read, 5000);
  }

  close() {
    this.closed = true;
    clearInterval(this.levelTimer); this.levelTimer = null;
    clearInterval(this.routeTimer); this.routeTimer = null;
    for (const id of Array.from(this.peers.keys())) this.removePeer(id);
    this.pendingSignals.clear();
    if (this.audioCtx) { this.audioCtx.close().catch(() => {}); this.audioCtx = null; }
    this.analysers.clear();
  }
}
